export interface Thinker {
  name: string;
  field: string;
  description: string;
  era: string;
  image: string;
  tags: string[];
}

export const thinkers: Thinker[] = [
  {
    name: "Leonardo da Vinci",
    field: "Art, Science & Engineering",
    description: "The ultimate polymath, painter, sculptor, architect, musician, mathematician, engineer, inventor, anatomist, geologist, and writer. Da Vinci's ability to move between disciplines produced works and ideas centuries ahead of his time.",
    era: "1452–1519",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Leonardo_self.jpg?width=300",
    tags: ["Polymath", "Art", "Science"],
  },
  {
    name: "Nikola Tesla",
    field: "Electrical Engineering & Physics",
    description: "A visionary inventor who imagined wireless communication, renewable energy, and alternating current decades before the world was ready. Tesla's thinking was famously nonlinear, he visualized complete inventions in his mind before ever building them.",
    era: "1856–1943",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/N.Tesla.JPG?width=300",
    tags: ["Inventor", "Visionary", "Neurodivergent"],
  },
  {
    name: "Marie Curie",
    field: "Physics & Chemistry",
    description: "The first person to win Nobel Prizes in two different sciences. Curie's relentless cross-disciplinary curiosity, bridging physics, chemistry, and medicine, led to groundbreaking discoveries in radioactivity that transformed healthcare forever.",
    era: "1867–1934",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Marie_Curie_c._1920s.jpg?width=300",
    tags: ["Pioneer", "Physics", "Chemistry"],
  },
  {
    name: "Albert Einstein",
    field: "Theoretical Physics",
    description: "Einstein didn't just think outside the box, he reimagined the box entirely. Dyslexic and a self-described visual thinker, he used thought experiments and imagination rather than traditional methods to reshape our understanding of space, time, and energy.",
    era: "1879–1955",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Albert_Einstein_Head.jpg?width=300",
    tags: ["Physics", "Dyslexic", "Visual Thinker"],
  },
  {
    name: "Frida Kahlo",
    field: "Art & Activism",
    description: "Kahlo transformed personal pain into universal art, blending surrealism, Mexican folk art, and raw autobiography. Her work defied categorization and challenged conventions of beauty, gender, and identity.",
    era: "1907–1954",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Frida_Kahlo,_by_Guillermo_Kahlo.jpg?width=300",
    tags: ["Art", "Activist", "Resilience"],
  },
  {
    name: "Ada Lovelace",
    field: "Mathematics & Computing",
    description: "Often called the first computer programmer, Lovelace saw the potential of Charles Babbage's Analytical Engine far beyond calculation. She envisioned machines creating music and art, a leap of imagination that wouldn't be realized for over a century.",
    era: "1815–1852",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Ada_Lovelace_portrait.jpg?width=300",
    tags: ["Computing", "Visionary", "Mathematics"],
  },
  {
    name: "Steve Jobs",
    field: "Technology & Design",
    description: "Jobs combined calligraphy, Zen Buddhism, and technology to revolutionize personal computing, music, phones, and retail. His genius lay in connecting dots across seemingly unrelated fields, the definition of nonlinear thinking.",
    era: "1955–2011",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Steve_Jobs_Headshot_2010-CROP_(cropped_2).jpg?width=300",
    tags: ["Technology", "Design", "Dyslexic"],
  },
  {
    name: "Maya Angelou",
    field: "Literature & Activism",
    description: "Poet, memoirist, actress, director, producer, playwright, and civil rights activist. Angelou's life was a masterclass in multipotentiality, she drew from every experience to create art that moved the world.",
    era: "1928–2014",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Angelou_at_Clinton_inauguration_(cropped_2).jpg?width=300",
    tags: ["Literature", "Activist", "Multipotentialite"],
  },
  {
    name: "Richard Branson",
    field: "Entrepreneurship",
    description: "Dyslexic and a school dropout, Branson built the Virgin empire spanning music, airlines, telecoms, and space travel. He credits his dyslexia as his greatest strength, forcing him to simplify, delegate, and think differently.",
    era: "1950–present",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Richard_Branson_March_2015_(cropped).jpg?width=300",
    tags: ["Entrepreneur", "Dyslexic", "ADHD"],
  },
  {
    name: "Simone de Beauvoir",
    field: "Philosophy & Literature",
    description: "Philosopher, novelist, and feminist icon who connected existential philosophy with gender politics, ethics, and social activism. Her groundbreaking 'The Second Sex' bridged philosophy, history, biology, and lived experience.",
    era: "1908–1986",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Simone_de_Beauvoir2.png?width=300",
    tags: ["Philosophy", "Feminist", "Writer"],
  },
  {
    name: "Benjamin Franklin",
    field: "Science, Politics & Writing",
    description: "Printer, scientist, inventor, diplomat, writer, and Founding Father. Franklin's restless curiosity led him from the printing press to electricity to bifocals to the Declaration of Independence, all in one extraordinary life.",
    era: "1706–1790",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Joseph_Siffrein_Duplessis_-_Benjamin_Franklin_-_Google_Art_Project.jpg?width=300",
    tags: ["Polymath", "Inventor", "Statesman"],
  },
  {
    name: "Temple Grandin",
    field: "Animal Science & Autism Advocacy",
    description: "Diagnosed with autism as a child, Grandin revolutionized livestock handling by thinking in pictures, literally seeing the world from an animal's perspective. She's become the world's most famous advocate for neurodiversity in science.",
    era: "1947–present",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Temple_Grandin_at_TED.jpg?width=300",
    tags: ["Science", "Autistic", "Visual Thinker"],
  },
  {
    name: "David Bowie",
    field: "Music, Art & Fashion",
    description: "Bowie reinvented himself endlessly, from Ziggy Stardust to the Thin White Duke to electronic pioneer. He blurred the lines between music, theater, visual art, and fashion, proving that creativity thrives on constant transformation.",
    era: "1947–2016",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/David-Bowie_Chicago_2002-08-08_photoby_Adam-Bielawski-cropped.jpg?width=300",
    tags: ["Music", "Art", "Reinvention"],
  },
  {
    name: "Hedy Lamarr",
    field: "Film & Invention",
    description: "Hollywood star by day, inventor by night. Lamarr co-invented frequency-hopping spread spectrum technology during WWII, the foundation of modern Wi-Fi and Bluetooth. The ultimate example of a nonlinear life.",
    era: "1914–2000",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Hedy_Lamarr_Publicity_Photo_for_The_Heavenly_Body_1944.jpg/330px-Hedy_Lamarr_Publicity_Photo_for_The_Heavenly_Body_1944.jpg",
    tags: ["Inventor", "Actress", "Pioneer"],
  },
  {
    name: "Buckminster Fuller",
    field: "Architecture, Design & Systems",
    description: "Architect, systems theorist, author, designer, inventor, and futurist. Fuller coined 'Spaceship Earth' and invented the geodesic dome by thinking about housing, mathematics, and nature simultaneously.",
    era: "1895–1983",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3b/BuckminsterFuller1.jpg/330px-BuckminsterFuller1.jpg",
    tags: ["Architecture", "Systems Thinker", "Inventor"],
  },
  {
    name: "John Lennon",
    field: "Music & Peace Activism",
    description: "Co-founder of The Beatles who used music as a vehicle for social change. Dyslexic and fiercely unconventional, Lennon merged rock, avant-garde art, and political activism into a singular creative force that shaped generations.",
    era: "1940–1980",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/John_Lennon_1969_(cropped).jpg?width=300",
    tags: ["Music", "Activist", "Dyslexic"],
  },
  {
    name: "Salvador Dalí",
    field: "Art & Film",
    description: "The master of surrealism who turned dreams, obsessions, and the subconscious into visual spectacles. Dalí worked across painting, sculpture, film, and photography, always defying logic and convention.",
    era: "1904–1989",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Salvador_Dalí_1939.jpg?width=300",
    tags: ["Art", "Surrealism", "Visionary"],
  },
  {
    name: "Billie Eilish",
    field: "Music & Culture",
    description: "At just 18, she swept the Grammys with music produced in a bedroom. Diagnosed with Tourette syndrome and synesthesia, Eilish channels her neurodivergence into genre-defying music and visuals that rewrote the rules of pop.",
    era: "2001–present",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Billie_Eilish_2019_by_Glenn_Francis_(cropped)_2.jpg?width=300",
    tags: ["Music", "Neurodivergent", "Gen Z"],
  },
  {
    name: "Martha Graham",
    field: "Dance & Choreography",
    description: "The mother of modern dance who revolutionized the art form by rejecting classical ballet's rigidity. Graham created a new language of movement rooted in raw emotion, breath, and the human experience.",
    era: "1894–1991",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Martha_Graham_1948.jpg?width=300",
    tags: ["Dance", "Pioneer", "Art"],
  },
  {
    name: "Emma Watson",
    field: "Acting & Activism",
    description: "From Hermione Granger to UN Women Goodwill Ambassador, Watson bridges entertainment and social justice. Her HeForShe campaign and advocacy for gender equality show how fame can be a platform for systemic change.",
    era: "1990–present",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Emma_Watson_2013.jpg?width=300",
    tags: ["Activist", "Actress", "Feminist"],
  },
  {
    name: "Robin Williams",
    field: "Comedy & Acting",
    description: "A mind that moved at lightning speed, Williams could improvise, connect, and transform faster than anyone. His ADHD-fueled genius produced comedy and drama that touched millions, from Mork to Good Will Hunting.",
    era: "1951–2014",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Robin_Williams_2011a_(2).jpg?width=300",
    tags: ["Comedy", "ADHD", "Multipotentialite"],
  },
  {
    name: "Pablo Picasso",
    field: "Art",
    description: "Co-founder of Cubism who shattered perspective and rebuilt it from every angle simultaneously. Picasso's relentless reinvention across painting, sculpture, ceramics, and printmaking made him the most influential artist of the 20th century.",
    era: "1881–1973",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Pablo_picasso_1.jpg?width=300",
    tags: ["Art", "Cubism", "Reinvention"],
  },
  {
    name: "Tinker Hatfield",
    field: "Design & Architecture",
    description: "American designer behind iconic Nike shoes including the Air Jordan series. Trained as an architect, Hatfield revolutionized footwear design by drawing inspiration from architecture, art, and culture, a true nonlinear thinker.",
    era: "Contemporary",
    image: "https://upload.wikimedia.org/wikipedia/commons/9/99/Tinker_Hatfield.jpg",
    tags: ["Creative", "Leadership", "Visionary"],
  },
  {
    name: "Frank Gehry",
    field: "Architecture",
    description: "The architect who made buildings dance. Gehry's deconstructivist designs, from the Guggenheim Bilbao to the Walt Disney Concert Hall, reject straight lines and rigid forms, proving that architecture can be as expressive as sculpture.",
    era: "1929–present",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Frank_Gehry%2C_Viljo_Revell_Centenary_reception%2C_2010_%28S2311_fl2969_it0089%29_%28cropped%29.jpg/330px-Frank_Gehry%2C_Viljo_Revell_Centenary_reception%2C_2010_%28S2311_fl2969_it0089%29_%28cropped%29.jpg",
    tags: ["Architecture", "Design", "Visionary"],
  },
  {
    name: "Elton John",
    field: "Music & Philanthropy",
    description: "One of the best-selling music artists of all time, Elton John merged flamboyant showmanship with deeply personal songwriting. His AIDS Foundation has raised over $600 million, proving that art and activism are inseparable.",
    era: "1947–present",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/61/EltonDocBFILFF101024_%284_of_17%29_%28cropped%29.jpg/330px-EltonDocBFILFF101024_%284_of_17%29_%28cropped%29.jpg",
    tags: ["Music", "Philanthropy", "LGBTQ+"],
  },
  {
    name: "Irvine Welsh",
    field: "Literature & Film",
    description: "The Edinburgh-born writer who gave voice to the voiceless with Trainspotting. Welsh's raw, dialect-driven prose broke every rule of 'proper' literature and brought working-class stories to the global stage.",
    era: "1958–present",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/Irvine_Welsh_2024_i109.jpg/330px-Irvine_Welsh_2024_i109.jpg",
    tags: ["Literature", "Film", "Counterculture"],
  },
  {
    name: "Peter Greenaway",
    field: "Film & Art",
    description: "A filmmaker who paints with cinema. Greenaway's visually stunning, intellectually demanding films blur the line between art installation and narrative cinema, challenging audiences to see film as a total art form.",
    era: "1942–present",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/af/Peter_Greenaway_in_2007.jpg/330px-Peter_Greenaway_in_2007.jpg",
    tags: ["Film", "Art", "Visionary"],
  },
  {
    name: "Prince",
    field: "Music & Performance",
    description: "A multi-instrumentalist, songwriter, producer, and performer who defied every genre boundary. Prince's fusion of funk, rock, pop, R&B, and electronic music, combined with his gender-fluid persona, made him one of the most original artists ever.",
    era: "1958–2016",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Prince_at_Coachella_(cropped).jpg?width=300",
    tags: ["Music", "Multipotentialite", "Reinvention"],
  },
  {
    name: "Willem de Kooning",
    field: "Art",
    description: "A leading figure of Abstract Expressionism who bridged figuration and abstraction. De Kooning's aggressive, gestural brushwork and refusal to commit to any single style made him one of the most dynamic painters of the 20th century.",
    era: "1904–1997",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/Willem_de_Kooning_in_his_studio.jpg/330px-Willem_de_Kooning_in_his_studio.jpg",
    tags: ["Art", "Abstract Expressionism", "Pioneer"],
  },
  {
    name: "Jackson Pollock",
    field: "Art",
    description: "The king of action painting who threw away the easel and dripped, poured, and splattered paint onto canvases on the floor. Pollock's radical process redefined what art could be and how it could be made.",
    era: "1912–1956",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Namuth_-_Pollock.jpg?width=300",
    tags: ["Art", "Abstract Expressionism", "Radical"],
  },
  {
    name: "Desmond Tutu",
    field: "Theology & Human Rights",
    description: "Nobel Peace Prize laureate who used moral clarity and infectious joy to dismantle apartheid. Tutu's nonlinear approach, blending theology, humor, and political activism, made him one of the most effective change-makers in history.",
    era: "1931–2021",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e4/Archbishop-Tutu-medium.jpg/330px-Archbishop-Tutu-medium.jpg",
    tags: ["Activist", "Peace", "Theology"],
  },
  {
    name: "Nelson Mandela",
    field: "Politics & Human Rights",
    description: "From prisoner to president, Mandela's 27 years of incarceration became a crucible for transformation. His ability to forgive, unite, and lead a nation away from apartheid remains the ultimate example of nonlinear resilience.",
    era: "1918–2013",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Nelson_Mandela_1994.jpg?width=300",
    tags: ["Activist", "Leader", "Resilience"],
  },
  {
    name: "Björk",
    field: "Music & Art",
    description: "The Icelandic artist who fuses electronic music, classical orchestration, nature sounds, and cutting-edge technology into otherworldly experiences. Björk's art exists in a category of one, perpetually ahead of its time.",
    era: "1965–present",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/Bjork_Orkestral_Paris_%28cropped%29.png/330px-Bjork_Orkestral_Paris_%28cropped%29.png",
    tags: ["Music", "Art", "Pioneer"],
  },
  {
    name: "Mahatma Gandhi",
    field: "Politics & Philosophy",
    description: "The architect of nonviolent resistance who proved that the most powerful force for change isn't violence, it's moral courage. Gandhi's philosophy of satyagraha influenced civil rights movements worldwide.",
    era: "1869–1948",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Mahatma-Gandhi,_studio,_1931.jpg?width=300",
    tags: ["Activist", "Philosophy", "Peace"],
  },
  {
    name: "Ai Weiwei",
    field: "Art & Activism",
    description: "China's most provocative contemporary artist who uses sculpture, architecture, film, and social media as weapons against authoritarianism. Ai Weiwei's art is inseparable from his activism, every piece is a political act.",
    era: "1957–present",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Ai_Weiwei.jpg?width=300",
    tags: ["Art", "Activist", "Dissident"],
  },
  {
    name: "Quentin Tarantino",
    field: "Film",
    description: "A high-school dropout and video store clerk who became cinema's most distinctive voice. Tarantino's films are nonlinear by design, fracturing timelines, blending genres, and remixing pop culture into something entirely new.",
    era: "1963–present",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Quentin_Tarantino_by_Gage_Skidmore.jpg?width=300",
    tags: ["Film", "Storyteller", "Autodidact"],
  },
  {
    name: "Andy Warhol",
    field: "Art & Culture",
    description: "The pope of Pop Art who blurred the line between commercial art and fine art, between celebrity and creation. Warhol's Factory was the ultimate nonlinear workspace, producing paintings, films, music, and cultural commentary simultaneously.",
    era: "1928–1987",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Andy_Warhol_1975.jpg?width=300",
    tags: ["Art", "Pop Art", "Visionary"],
  },
  {
    name: "Carrie Fisher",
    field: "Acting & Writing",
    description: "Princess Leia was just the beginning. Fisher was a bestselling novelist, script doctor, mental health advocate, and the sharpest wit in Hollywood. She turned her struggles with bipolar disorder into art, humor, and hope for millions.",
    era: "1956–2016",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Carrie_Fisher_2013-a_straightened.jpg?width=300",
    tags: ["Actress", "Writer", "Mental Health"],
  },
  {
    name: "Vivienne Westwood",
    field: "Fashion & Activism",
    description: "The queen of punk fashion who turned rebellion into haute couture. Westwood didn't just design clothes, she weaponized fashion as protest, championing climate action and human rights until her final days.",
    era: "1941–2022",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1c/Vivienne_Westwood_by_Mattia_Passeri.jpg/330px-Vivienne_Westwood_by_Mattia_Passeri.jpg",
    tags: ["Fashion", "Activist", "Punk"],
  },
  {
    name: "Rick Rubin",
    field: "Music Production",
    description: "The most eclectic producer in music history, from Beastie Boys to Johnny Cash, from Slayer to Adele. Rubin's genius lies in stripping away everything unnecessary and finding the essence, regardless of genre.",
    era: "1963–present",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/43/RickRubinSept09.jpg/330px-RickRubinSept09.jpg",
    tags: ["Music", "Producer", "Minimalist"],
  },
  {
    name: "Jane Goodall",
    field: "Primatology & Conservation",
    description: "She went into the jungle with no degree and revolutionized our understanding of chimpanzees, and ourselves. Goodall's empathy-driven science broke every rule of the field and proved that observation and connection matter more than credentials.",
    era: "1934–2025",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Jane_Goodall_2015.jpg?width=300",
    tags: ["Science", "Conservation", "Pioneer"],
  },
  {
    name: "Elon Musk",
    field: "Engineering & Entrepreneurship",
    description: "A serial founder who reasons from first principles, Musk has pushed boundaries across electric vehicles, reusable rockets, and brain-computer interfaces. His willingness to question established assumptions exemplifies nonlinear, cross-disciplinary thinking.",
    era: "1971–present",
    image: "https://en.wikipedia.org/wiki/Special:FilePath/Elon_Musk_Royal_Society_(crop2).jpg?width=300",
    tags: ["Engineering", "Entrepreneur", "Visionary"],
  },
  {
    name: "Sabrina Gonzalez Pasterski",
    field: "Theoretical Physics & Holography",
    description: "Dubbed the 'millennial Einstein,' Pasterski was already building single-engine aircraft in her teens and later earned a physics PhD from Harvard. She explores the nature of spacetime and celestial holography, showing that the deepest questions in the universe can be pursued by anyone who refuses to follow a straight line.",
    era: "1993–present",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/Sabrina_Gonzalez_Pasterski_2015.jpg/500px-Sabrina_Gonzalez_Pasterski_2015.jpg",
    tags: ["Physics", "Holography", "Pioneer"],
  },
  {
    name: "Nirmal \"Nims\" Purja",
    field: "High-Altitude Mountaineering",
    description: "He climbed all fourteen 8,000-metre peaks in six months and six days, shattering a record everyone thought would take years. Purja rejected the accepted logistics of Himalayan climbing, rebuilt it around speed, teamwork and Nepali climbers, and proved that 'impossible' is usually just an untested assumption.",
    era: "1983–2026",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8f/NIRMAL_PURJA_%22NIMS%22.jpg/500px-NIRMAL_PURJA_%22NIMS%22.jpg",
    tags: ["Mountaineering", "Endurance", "Record Breaker"],
  },
];
